/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import _ from "lodash";
import { useWindowSize } from "../ProjectState/ProjectUtils";
import React from "react";

export const deepClone = <T>(value: T): T => _.cloneDeep(value);

export function boundWithinRange(
  value: number,
  lowerLimit: number,
  upperLimit: number
): number {
  return Math.min(Math.max(value, lowerLimit), upperLimit);
}

// TypeScript workaround for missing window.navigator.msSaveBlob for the fileDownload function
// see https://stackoverflow.com/questions/69485778/new-typescript-version-does-not-include-window-navigator-mssaveblob?noredirect=1&lq=1
declare global {
  interface Navigator {
    msSaveBlob?: (blob: any, defaultName?: string) => boolean;
  }
}

// This function comes from https://github.com/kennethjiang/js-file-download
// and is licensed under the MIT license
export function fileDownload(
  data: string | ArrayBuffer | ArrayBufferView | Blob,
  filename: string,
  mime?: string,
  bom?: string | Uint8Array
): void {
  const blobData = typeof bom !== "undefined" ? [bom, data] : [data];
  const blob = new Blob(blobData, { type: mime || "application/octet-stream" });
  if (typeof window.navigator.msSaveBlob !== "undefined") {
    // IE workaround for "HTML7007: One or more blob URLs were
    // revoked by closing the blob for which they were created.
    // These URLs will no longer resolve as the data backing
    // the URL has been freed."
    window.navigator.msSaveBlob(blob, filename);
  } else {
    const blobURL =
      window.URL && window.URL.createObjectURL
        ? window.URL.createObjectURL(blob)
        : window.webkitURL.createObjectURL(blob);
    const tempLink = document.createElement("a");
    tempLink.style.display = "none";
    tempLink.href = blobURL;
    tempLink.setAttribute("download", filename);

    // Safari thinks _blank anchor are pop ups. We only want to set _blank
    // target if the browser does not support the HTML5 download attribute.
    // This allows you to download files in desktop safari if pop up blocking
    // is enabled.
    if (typeof tempLink.download === "undefined") {
      tempLink.setAttribute("target", "_blank");
    }

    document.body.appendChild(tempLink);
    tempLink.click();

    // Fixes "webkit blob resource error 1"
    setTimeout(function () {
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(blobURL);
    }, 200);
  }
}

export function getHandleOffsetTop(element: HTMLElement): number {
  let offsetTop = 0;
  // eslint-disable-next-line no-loops/no-loops
  while (element) {
    // we only want to iterate up to the node container
    if (element.className.includes("react-flow__node")) {
      break;
    }
    offsetTop += element.offsetTop;
    element = element.offsetParent as HTMLElement;
  }
  return offsetTop;
}

export function getHandleOffsetLeft(element: HTMLElement): number {
  let offsetLeft = 0;
  // eslint-disable-next-line no-loops/no-loops
  while (element) {
    // we only want to iterate up to the node container
    if (element.className.includes("react-flow__node")) {
      break;
    }
    offsetLeft += element.offsetLeft;
    element = element.offsetParent as HTMLElement;
  }
  return offsetLeft;
}

export enum AiStatus {
  Pending = "pending",
  Success = "success",
  Failed = "failed",
}

export enum Endpoints {
  SeedUserDemo = "/seedUserDemo",
  AddProject = "/addProject",
  ClearInputDescriptors = "clearInputNodeDescriptors",
  CreateInputNodeDescriptor = "/createInputNodeDescriptor",
  DeletePdfFileInfos = "/deletePdfFileInfos",
  DeleteInputDescriptor = "/deleteInputNodeDescriptor",
  DeleteProject = "/deleteProject",
  DetectFileType = "/detectFileType",
  DownloadFile = "/downloadFile?fileId",
  DownloadZip = "/downloadZip",
  DownloadFileToken = "/downloadFileToken?fileId",
  EnsureProjectFiles = "/ensureProjectFiles",
  FindSavedMappingsWithFileId = "/findSavedMappingsWithFileId",
  GetInputNodeDescriptors = "/getInputNodeDescriptors",
  GetInputNodeDescriptor = `/getInputNodeDescriptor`,
  GetTransformNodeDescriptors = "/getTransformNodeDescriptors",
  GetTransformTypeCounts = "/getTransformNodeCounts",
  GetTransformRecommendations = "/getTransformRecommendations",
  GetRefreshToken = "/refresh_token",
  GetProjects = "/getProjects",
  GetFileAliasIds = "/getFileAliasIds",
  GetStoredFileId = "/getStoredFileId",
  GetDemoMappingJson = "/demoMappingJson",
  GetOutputNodeDescriptor = "/getOutputNodeDescriptor",
  GetComposerNodeDescriptor = "/getComposerNodeDescriptor",
  InitializeApp = "/initializeApp",
  IsAppInitialized = "/isAppInitialized",
  Login = "/login",
  LogOut = "/logout",
  Register = "/register",
  ChangePassword = "/changePassword",
  ParseFiles = "/parseFiles",
  PerformMapping = "/performMapping",
  ResetRecommendationSession = "/resetRecommendationSession",
  RestoreInputNodeDescriptors = "/restoreInputNodeDescriptors",
  UpdateInputNodeDescriptorType = "/updateInputNodeDescriptorType",
  UploadFile = "/upload",
  GetParsedPdfFiles = "/getParsedPdfFiles",

  // QuickForm Endpoints
  QuickFormUpload = "/quickform/upload",
  QuickFormUpdateType = "/quickform/update-type",
  QuickFormUpdateTransformTo = "/quickform/update-transform-to",
  QuickFormTransformBulk = "/quickform/transform-bulk",
  QuickFormDownload = "/quickform/download",
  QuickFormDownloadZip = "/quickform/download-zip",
  QuickFormCleanup = "/quickform/cleanup",
  QuickFormDetectType = "/quickform/detect-type",
}

export const PROTO_PRIMITIVE_TYPES = [
  "string",
  "int32",
  "int64",
  "uint32",
  "uint64",
  "sint32",
  "sint64",
  "fixed32",
  "fixed64",
  "sfixed32",
  "sfixed64",
  "bool",
  "bytes",
  "float",
  "double",
] as const;

export const PROTO_MODIFIERS = ["required", "optional", "repeated"] as const;

export const PROTO_XML_FIELD_TAGS = [
  "modifier",
  "type",
  "name",
  "number",
] as const;

export const PROTO_STRUCTURES = [
  "message",
  "enum",
  "service",
  "oneof",
  "rpc",
  "map",
] as const;

export type NodeStructureType = (typeof PROTO_STRUCTURES)[number] | "field";

export const XML_TOKENS = {
  PROCESSING_INSTRUCTION: "<?",
  COMMENT: "<!--",
  CLOSE_TAG: "</",
  OPEN_TAG: "<",
  SELF_CLOSE: "/>",
  XML_DECLARATION: "<?xml",
} as const;

export const PROTO_TOKENS = {
  SYNTAX_DECLARATION: /^\s*syntax\s*=\s*["']proto/,
} as const;

export const acceptedFileTypes =
  "text/x-java-source,.java,text/x-c,.c,text/x-c++src,.cpp,.idl,.h,.proto, text/x-python, .py, application/xml, .xml, application/javascript, .js,.pb2,.pdf, .zip, .tar.gz";

export interface FileTypeOption {
  id: string;
  label: string;
  extension: string;
}

export const fileTypeOptionsFilesTable: FileTypeOption[] = [
  { id: "C", label: "C (.c)", extension: ".c" },
  { id: "CPP", label: "C++ (.cpp)", extension: ".cpp" },
  {
    id: "IDL",
    label: "Interface Definition Language (.idl)",
    extension: ".idl",
  },
  { id: "H", label: "Header File(.h)", extension: ".h" },
  { id: "PROTO", label: "Protocol Buffers (.proto)", extension: ".proto" },
  { id: "PY", label: "Python (.py)", extension: ".py" },
  { id: "XML", label: "Extensible Markup Language (.xml)", extension: ".xml" },
  { id: "JAVA", label: "Java (.java)", extension: ".java" },
  { id: "JS", label: "Javascript (.js)", extension: ".js" },
  { id: "PB2", label: "PB2 (.pb2)", extension: ".pb2" },
];

export const fileTypeOptionsMappingCanvasUpload: FileTypeOption[] = [
  { id: "C", label: "C", extension: ".c" },
  { id: "CPP", label: "C++", extension: ".cpp" },
  {
    id: "IDL",
    label: "IDL",
    extension: ".idl",
  },
  { id: "H", label: "H", extension: ".h" },
  { id: "PROTO", label: "PROTO", extension: ".proto" },
  { id: "PY", label: "PY", extension: ".py" },
  { id: "XML", label: "XML", extension: ".xml" },
  { id: "JAVA", label: "JAVA", extension: ".java" },
  { id: "JS", label: "JS", extension: ".js" },
  { id: "PB2", label: "PB2", extension: ".pb2" },
];

export const TYPE_OPTIONS =
  fileTypeOptionsMappingCanvasUpload as FileTypeOption[];

export const getExtension = (name: string): string => {
  const match = name.match(/\.[^./]+$/);
  return match ? match[0].toLowerCase() : "";
};

export const replaceExtension = (
  name: string,
  newExtension: string
): string => {
  const ext = getExtension(name);
  return ext ? name.slice(0, -ext.length) + newExtension : name + newExtension;
};

export const useListMaxHeight = (
  reservedHeight: number,
  minListHeight: number
): number => {
  const { height } = useWindowSize();
  return React.useMemo(
    () => Math.max(minListHeight, height - reservedHeight),
    [height, reservedHeight, minListHeight]
  );
};
